import type { ActiveOrganizationSignatureSummary } from '@pillar-portal/api-types';

export const ORGANIZATION_SIGNING_PATH: string = '/organization-agreements/sign';

const PROMPTED_STORAGE_KEY_PREFIX: string = 'pillar.organizationSigningPrompted.';

export type OrganizationSigningPromptStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function buildOrganizationSigningPath(returnTo: string): string {
    return `${ORGANIZATION_SIGNING_PATH}?returnTo=${encodeURIComponent(returnTo)}`;
}

/** A PRINCIPAL who can sign is sent to the signing screen when agreements await signature. */
export function shouldPromptOrganizationSigning(
    summary: ActiveOrganizationSignatureSummary | undefined,
    pathname: string
): boolean {
    return (
        summary?.summary_kind === 'organization' &&
        summary.can_sign &&
        summary.awaiting_signature_count > 0 &&
        !pathname.startsWith(ORGANIZATION_SIGNING_PATH)
    );
}

/**
 * Records that the post-login signing prompt was shown for this RoleAssignment
 * in this browser session. Returns true only the first time, so Sign later
 * keeps the PRINCIPAL in the application. Unavailable storage never prompts
 * repeatedly: it prompts at most once per page load instead.
 */
export function claimOrganizationSigningPrompt(
    storage: OrganizationSigningPromptStorage | null,
    roleAssignmentUuid: string,
    promptedInPageLoad: Set<string>
): boolean {
    if (promptedInPageLoad.has(roleAssignmentUuid)) {
        return false;
    }
    promptedInPageLoad.add(roleAssignmentUuid);
    const storageKey = `${PROMPTED_STORAGE_KEY_PREFIX}${roleAssignmentUuid}`;
    try {
        if (storage === null) {
            return true;
        }
        if (storage.getItem(storageKey) !== null) {
            return false;
        }
        storage.setItem(storageKey, '1');
        return true;
    } catch {
        return true;
    }
}

export function awaitingSignatureMessage(
    summary: Extract<ActiveOrganizationSignatureSummary, { summary_kind: 'organization' }>
): string {
    const count = summary.awaiting_signature_count;
    if (summary.can_sign) {
        return count === 1
            ? `${summary.organization_name} has 1 agreement awaiting your signature.`
            : `${summary.organization_name} has ${count} agreements awaiting your signature.`;
    }
    return count === 1
        ? `${summary.organization_name} has 1 agreement awaiting signature by a principal.`
        : `${summary.organization_name} has ${count} agreements awaiting signature by a principal.`;
}
